import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type FlowPhase = "assessment" | "selection" | "supply";

const PHASES: { id: FlowPhase; label: string }[] = [
  { id: "assessment", label: "Assessment" },
  { id: "selection", label: "Selection" },
  { id: "supply", label: "Supply" },
];

interface FlowProgressProps {
  phase: FlowPhase;
  step: number;
  totalSteps: number;
  stepTitle?: string;
  className?: string;
  isDarkMode?: boolean;
}

export function flowProgressValue(
  phase: FlowPhase,
  step: number,
  totalSteps: number,
) {
  const phaseIndex = PHASES.findIndex((p) => p.id === phase);
  const within = totalSteps > 0 ? Math.min(Math.max(step / totalSteps, 0), 1) : 0;
  return Math.round(((phaseIndex + within) / PHASES.length) * 100);
}

export function FlowProgress({
  phase,
  step,
  totalSteps,
  stepTitle,
  className,
  isDarkMode = false,
}: FlowProgressProps) {
  const phaseIndex = PHASES.findIndex((p) => p.id === phase);
  const within = totalSteps > 0 ? Math.min(Math.max(step / totalSteps, 0), 1) : 0;
  const currentLabel = PHASES[phaseIndex]?.label ?? "";
  const valueText = `${currentLabel}, step ${step} of ${totalSteps}${
    stepTitle ? `: ${stepTitle}` : ""
  }`;

  return (
    <div
      className={cn("space-y-2", className)}
      data-testid="travel-flow-progress"
      role="progressbar"
      aria-label="Consultation progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={flowProgressValue(phase, step, totalSteps)}
      aria-valuetext={valueText}
    >
      <div className="grid grid-cols-3 gap-2">
        {PHASES.map((p, index) => {
          const done = index < phaseIndex;
          const active = index === phaseIndex;
          const fill = done ? 100 : active ? within * 100 : 0;
          return (
            <div
              key={p.id}
              data-phase={p.id}
              data-state={done ? "done" : active ? "active" : "upcoming"}
              aria-current={active ? "step" : undefined}
              className="space-y-1.5"
            >
              <span
                className={cn(
                  "flex items-center gap-1 text-xs font-medium",
                  active
                    ? isDarkMode ? "text-white" : "text-gray-900"
                    : done
                      ? isDarkMode ? "text-gray-300" : "text-gray-700"
                      : isDarkMode ? "text-gray-500" : "text-gray-400",
                )}
              >
                {done && (
                  <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
                )}
                {p.label}
              </span>
              <span className={cn("block h-1.5 overflow-hidden rounded-full", isDarkMode ? "bg-gray-700" : "bg-gray-200")}>
                <span
                  className={cn(
                    "block h-full rounded-full transition-all duration-300",
                    done ? "bg-emerald-600" : isDarkMode ? "bg-white" : "bg-slate-900",
                  )}
                  style={{ width: `${fill}%` }}
                />
              </span>
            </div>
          );
        })}
      </div>
      <p className={cn("text-sm", isDarkMode ? "text-gray-400" : "text-gray-500")} data-testid="travel-flow-step-label">
        Step {step} of {totalSteps}
        {stepTitle ? <span className={isDarkMode ? "text-gray-300" : "text-gray-700"}> · {stepTitle}</span> : null}
      </p>
    </div>
  );
}
